import { useMount } from 'ahooks'
import * as R from 'ramda'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { LayoutScroll } from 'components/LayoutPage'
import Divider from 'components/uiKit/Divider'
import type { KitDimensions, KitSize, KitIndents, ValueType } from 'components/uiKit/KitTypes'
import { doScroll } from 'services/Scroll/ScrollService'
import { t } from 'services/Translation'

import type { CourseValueType, ICourseOption } from '../CourseKitTypes'
import CourseMenuItem from './CourseMenuItem'
import type { IMenuContext } from './MenuContext'
import MenuContext from './MenuContext'

export interface ICourseMenuProps<V extends CourseValueType> {
  onClick?: (item: ICourseOption<V>) => void
  options: ICourseOption<V>[]
  title?: React.ReactNode
  value?: V | null
  empty?: React.ReactNode
  size?: KitSize
  target?: HTMLButtonElement | null
  dimensions?: KitDimensions
  indents?: KitIndents
  resettable?: boolean
  initScrollId?: string
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseOver?: (e: React.MouseEvent<HTMLDivElement>) => void
  focusedId?: string | number
  id: string
}

export interface IMenuRef<V extends ValueType> {
  setHovered: React.Dispatch<React.SetStateAction<ICourseOption<V> | null>>
}

const Menu = ({
  onClick,
  onMouseLeave,
  onMouseOver,
  options,
  title,
  value,
  dimensions,
  indents,
  empty = t('uiKit.menu.empty'),
  initScrollId,
  focusedId,
  id,
}: ICourseMenuProps<ValueType>) => {
  const ref = useRef<HTMLDivElement>(null)
  const scrollRef = useRef(null)
  const [hovered, setHovered] = useState<ICourseOption<ValueType> | null>(null)

  const height = useMemo(
    () => dimensions && R.pick(['height', 'minHeight', 'maxHeight'], dimensions),
    [dimensions],
  )
  const width = useMemo(
    () => dimensions && R.pick(['width', 'minWidth', 'maxWidth'], dimensions),
    [dimensions],
  )

  const onHover = useCallback(
    (option: ICourseOption<ValueType> | null) => {
      setHovered(option)
      doScroll(scrollRef.current, option?.value, { margin: 4, duration: 300 })
    },
    [scrollRef],
  )

  const handleMouseLeave = () => setHovered(null)

  useMount(() => {
    if (initScrollId) {
      doScroll(scrollRef.current, initScrollId, {
        block: 'begin',
        scroll: 'always',
        duration: 300,
      })
    } else {
      onHover(options.find((item) => item.value === value) || null)
    }
  })

  const contextValue: IMenuContext<ValueType> = useMemo(
    () => ({ value, hovered, setHovered }),
    [value, hovered],
  )

  if (!options?.length || options.every((item) => item.hidden)) {
    return <span>{empty}</span>
  }

  return (
    <MenuContext.Provider value={contextValue}>
      <div onMouseLeave={onMouseLeave} onMouseOver={onMouseOver} ref={ref} style={width}>
        {title && (
          <>
            <div>{title}</div>
            <Divider free />
          </>
        )}
        <LayoutScroll ref={scrollRef} autoUpdate sizeAutoCapable>
          <div
            id={id}
            onMouseLeave={handleMouseLeave}
            role='listbox'
            style={{ ...height, ...indents }}
            tabIndex={-1}
          >
            {options.map((item) => (
              <CourseMenuItem
                isFocused={focusedId === item.id}
                item={item}
                key={String(item.id)}
                onClick={onClick}
              />
            ))}
          </div>
        </LayoutScroll>
      </div>
    </MenuContext.Provider>
  )
}

Menu.displayName = 'Menu'

export default React.memo(Menu) as <V extends ValueType>(
  props: ICourseMenuProps<V> & { ref?: React.RefObject<IMenuRef<V>> },
) => ReturnType<typeof Menu>
